import './index.css';

import { initializeSentry } from '@jablotron/lib.spa-sentry';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AppWrappers } from './AppWrappers';
import { GeneralError } from './components/errors/GeneralError';
import { DefaultPendingComponent } from './components/router/DefaultPendingComponent';
import { config, loadEnvironmentConfig } from './config/config';
import { type AppRouter, createAppRouter } from './router';
import { isLocal } from './utils/isLocal';
import { reloadOnStaleChunk } from './utils/reloadOnStaleChunk';

export const ROOT_ELEMENT_ID = 'root';

reloadOnStaleChunk();

const rootElement = document.getElementById(ROOT_ELEMENT_ID)!;
const root = createRoot(rootElement);

const App: React.FC<{ router: AppRouter }> = ({ router }) => (
  <StrictMode>
    <AppWrappers urlLanguage={router.urlLanguage}>
      <RouterProvider router={router} />
    </AppWrappers>
  </StrictMode>
);

const Bootstrap: React.FC = () => {
  const [router, setRouter] = useState<AppRouter | null>(null);
  const [error, setError] = useState<unknown>();

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const environmentConfig = await loadEnvironmentConfig();
        const appRouter = createAppRouter();

        if (!isLocal && environmentConfig.environment !== 'development') {
          initializeSentry({
            ...config.sentry,
            environment: environmentConfig.environment as Parameters<typeof initializeSentry>[0]['environment'],
            release: import.meta.env.VITE_APP_VERSION,
            integrations: { tanstackRouter: appRouter },
          });
        }

        setRouter(appRouter);
      } catch (bootstrapError) {
        setError(bootstrapError);
      }
    };

    void bootstrap();
  }, []);

  if (error) {
    return <GeneralError />;
  }

  if (!router) {
    return <DefaultPendingComponent />;
  }

  return <App router={router} />;
};

root.render(<Bootstrap />);
